/**
 * Compares the study type a paper states for itself (PubMed's publication types) with the
 * study_type we annotated from its primary finding, so the two can be shown side by side when
 * they differ.
 *
 * Each publication type maps to the study_type leaves it is compatible with. A type that says
 * nothing about design is absent from the table and never produces a mismatch.
 */
import type { StudyTypeLabel } from "@shared/scorecard";

const TRIAL_STUDY_TYPES: readonly StudyTypeLabel[] = [
  "RCT",
  "Non-Randomised Controlled Trial",
  "Uncontrolled Trial",
];

const OBSERVATIONAL_STUDY_TYPES: readonly StudyTypeLabel[] = [
  "Comparative Prospective Cohort",
  "Comparative Retrospective Cohort",
  "Uncontrolled Prospective Cohort",
  "Uncontrolled Retrospective Cohort",
  "Case-Control",
  "Cross-Sectional",
  "Other Observational",
];

/** The study_type leaves each PubMed publication type is compatible with, as NCBI spells it. */
export const PUBLICATION_TYPE_STUDY_TYPES: Readonly<Record<string, readonly StudyTypeLabel[]>> = {
  "Randomized Controlled Trial": ["RCT"],
  "Controlled Clinical Trial": ["RCT", "Non-Randomised Controlled Trial"],
  "Non-Randomized Controlled Trial": ["Non-Randomised Controlled Trial"],
  "Clinical Trial": TRIAL_STUDY_TYPES,
  "Clinical Trial, Phase I": TRIAL_STUDY_TYPES,
  "Clinical Trial, Phase II": TRIAL_STUDY_TYPES,
  "Clinical Trial, Phase III": TRIAL_STUDY_TYPES,
  "Clinical Trial, Phase IV": TRIAL_STUDY_TYPES,
  "Pragmatic Clinical Trial": TRIAL_STUDY_TYPES,
  "Equivalence Trial": TRIAL_STUDY_TYPES,
  "Adaptive Clinical Trial": TRIAL_STUDY_TYPES,
  "Observational Study": OBSERVATIONAL_STUDY_TYPES,
  "Observational Study, Veterinary": OBSERVATIONAL_STUDY_TYPES,
  "Case Reports": ["Anecdotal", "Other Observational"],
};

/** The outcome of comparing a document's stated publication types with its annotated study_type. */
export interface StatedStudyTypeComparison {
  /** The publication type to show as "stated as", or null when none states a design. */
  statedPublicationType: string | null;
  /** True only when a design is stated and the annotated study_type is not compatible with it. */
  isMismatch: boolean;
}

/**
 * Compares a document's PubMed publication types with its annotated study_type.
 * A generic type is ignored when a narrower one is present; conflicting narrow types agree
 * with the annotation if either is compatible.
 */
export function compareStatedStudyType(
  publicationTypes: readonly string[],
  annotatedStudyType: StudyTypeLabel
): StatedStudyTypeComparison {
  const narrowest = selectNarrowestStatedTypes(publicationTypes);
  if (narrowest.length === 0) {
    return { statedPublicationType: null, isMismatch: false };
  }
  const isCompatible = narrowest.some(({ studyTypes }) => studyTypes.includes(annotatedStudyType));
  return { statedPublicationType: narrowest[0].publicationType, isMismatch: !isCompatible };
}

interface StatedType {
  publicationType: string;
  studyTypes: readonly StudyTypeLabel[];
}

/** Keeps the types that state a design, minus any whose leaves strictly contain another's, narrowest first. */
function selectNarrowestStatedTypes(publicationTypes: readonly string[]): StatedType[] {
  const stated: StatedType[] = [];
  for (const publicationType of new Set(publicationTypes)) {
    const studyTypes = PUBLICATION_TYPE_STUDY_TYPES[publicationType];
    if (studyTypes) stated.push({ publicationType, studyTypes });
  }
  return stated
    .filter((candidate) => !stated.some((other) => isStrictSuperset(candidate, other)))
    .sort((a, b) => a.studyTypes.length - b.studyTypes.length);
}

function isStrictSuperset(candidate: StatedType, other: StatedType): boolean {
  return (
    candidate.studyTypes.length > other.studyTypes.length &&
    other.studyTypes.every((studyType) => candidate.studyTypes.includes(studyType))
  );
}
